/**
 * 画布左下角页面导航：触发按钮 + 向上弹出的缩略图面板
 * 定位由 CanvasDesignerOverlays 负责（与 BottomRightToolBar 一致）
 */
import { useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconListDetails, IconPin, IconPlus } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { Button, ScrollShadow, cn } from '@heroui/react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { useAddPage } from '@/components/canvas/page/controllers/useAddPage';
import { useWorkspaceChrome } from '@/components/canvas/workspace/stage/WorkspaceChromeContext';
import { CanvasThumbnailsBar } from '@/components/canvas/designer/thumbnails/ThumbnailsBar';

const PANEL_MOTION = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 24 },
  transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
};

export interface BottomLeftToolbarProps {
  readonly?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function BottomLeftToolbar({ readonly = false, className, style }: BottomLeftToolbarProps) {
  const chrome = useWorkspaceChrome();
  const { pages } = useCanvas();
  const addPage = useAddPage();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || pinned) return;
    const close = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close, true);
    return () => document.removeEventListener('mousedown', close, true);
  }, [open, pinned]);

  if (chrome?.isFullscreen) return null;

  const pageCount = pages.length;

  const toggleOpen = () => setOpen((v) => !v);

  return (
    <div
      ref={rootRef}
      className={cn('pointer-events-auto flex flex-col items-start', className)}
      style={style}
    >
      <AnimatePresence>
        {open ? (
          <motion.div
            key="pages-nav-panel"
            id="pages-nav-panel"
            role="dialog"
            aria-label="页面导航"
            className="mb-2 flex min-h-0 w-[min(92vw,220px)] h-[calc(100dvh-5rem-4rem)] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-md"
            {...PANEL_MOTION}
          >
            <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1">
              <span className="text-sm text-muted">共{pageCount}张</span>
              <div className="flex items-center gap-0.5">
                {!readonly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    isIconOnly
                    className="size-8"
                    aria-label="添加页面"
                    onPress={addPage}
                  >
                    <IconPlus className="size-4" stroke={1.25} />
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant={pinned ? 'secondary' : 'ghost'}
                  size="sm"
                  isIconOnly
                  className="size-8"
                  aria-label={pinned ? '取消固定' : '固定面板'}
                  aria-pressed={pinned}
                  onPress={() => setPinned((v) => !v)}
                >
                  <IconPin className="size-4" stroke={1.25} />
                </Button>
              </div>
            </header>
            <ScrollShadow
              orientation="vertical"
              className="min-h-0 flex-1 px-2 py-2"
              hideScrollBar
            >
              <CanvasThumbnailsBar layout="vertical" className="w-full" />
            </ScrollShadow>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <Button
        type="button"
        variant="tertiary"
        size="sm"
        className="gap-2"
        aria-label="页面导航"
        aria-expanded={open}
        aria-controls={open ? 'pages-nav-panel' : undefined}
        onPress={toggleOpen}
      >
        <IconListDetails className="size-4 shrink-0" stroke={1.25} />
        页面导航
        <IconChevronDown
          className={cn('size-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
          stroke={1.25}
        />
      </Button>
    </div>
  );
}
